import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchGetActiveMissions,
  getFleetIdsRelatesToVehiclePage,
  setInitialDriveModeStatuses,
  setVehiclesPageVehicles,
  updateVehicleDriveModeStatus,
  updateVehiclesPageMissions,
} from "Slices/vehiclesPage";
import { getCurrentSemanticMapId } from "Slices/appState";
import { getVehicles } from "Slices/vehicles";
import { useFMSWebsocket } from "Utils/webSocketUtils";
import {
  VEHICLE_LIST_MISSIONS_WS,
  VEHICLE_LIST_STATE_WS,
  WS_SPINNER_LOGS,
} from "Utils/constants";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:VehicleList`);

export const VehiclesListController = memo(() => {
  const dispatch = useDispatch();

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    []
  );

  const [isVehiclesWSConnected, setIsVehiclesWSConnected] = useState(false);
  const [isMissionWSConnected, setIsMissionWSConnected] = useState(false);

  useEffect(() => {
    if (isVehiclesWSConnected && isMissionWSConnected) {
      wsSpinnerLogs.debug(
        "isVehiclesWSConnected and isMissionWSConnected spinner off"
      );
      setIsWSConnecting?.(false);
    } else {
      wsSpinnerLogs.debug(
        "no isVehiclesWSConnected or no isMissionWSConnected spinner on"
      );
      setIsWSConnecting?.(true);
    }
  }, [isVehiclesWSConnected, isMissionWSConnected]);

  const currentSemanticMapId = useSelector(getCurrentSemanticMapId);
  useEffect(() => {
    setIsVehiclesWSConnected(false);
  }, [currentSemanticMapId]);

  const fleetIdsRelatesToVehiclePage = useSelector(
    getFleetIdsRelatesToVehiclePage
  );
  const vehicles = useSelector(getVehicles);

  useEffect(() => {
    dispatch(setVehiclesPageVehicles(vehicles));
  }, [vehicles]);

  //prevents recalculations of component due to fleetIdsRelatesToVehiclePage is affected by vehicles inside selector getFleetIdsRelatesToVehiclePage
  const currentFleetIds = useMemo(() => {
    setIsMissionWSConnected(false);
    return fleetIdsRelatesToVehiclePage;
  }, [JSON.stringify(fleetIdsRelatesToVehiclePage)]);

  useEffect(() => {
    if (!currentFleetIds) return;

    dispatch(fetchGetActiveMissions(currentFleetIds));
  }, [currentFleetIds]);

  useFMSWebsocket({
    url: "/fleets/mission_queue/",
    subscriptionData: currentFleetIds,
    loggerFlag: VEHICLE_LIST_MISSIONS_WS,
    getDataForSubscribe: (subscriptionData) => ({
      fleet_ids: subscriptionData,
    }),
    getDataForUnsubscribe: (subscriptionData) => ({
      fleet_ids: subscriptionData,
    }),
    handleInitialMessage: () => {
      setIsMissionWSConnected(true);
    },
    handleMessage: (data) => {
      const shouldIgnoreUpdate = data?.state === "STATE_RUNNING";
      if (shouldIgnoreUpdate) return;

      dispatch(updateVehiclesPageMissions(data));
    },
  });

  useFMSWebsocket({
    url: "/vehicles/state/",
    subscriptionData: currentSemanticMapId,
    loggerFlag: VEHICLE_LIST_STATE_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
      fields: ["t_drive_mode"],
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
    }),
    handleInitialMessage: (data: VehicleState[]) => {
      setIsVehiclesWSConnected(true);
      const driveModeStateByVehicleId = data.reduce(
        (acc: Record<string, string>, curr) => {
          if (curr?.online_status === "OFFLINE") {
            acc[curr.vehicle_id] = "OFFLINE";
          } else {
            if (curr?.t_drive_mode !== null)
              acc[curr.vehicle_id] = curr?.t_drive_mode;
          }

          return acc;
        },
        {}
      );
      dispatch(setInitialDriveModeStatuses(driveModeStateByVehicleId));
    },
    handleMessage: (data: VehicleState) => {
      dispatch(updateVehicleDriveModeStatus(data));
    },
  });

  return null;
});
